import React, { useEffect, useMemo } from 'react';
import { Flex, Text } from '@podiumhq/design-system';
import { useTracking } from '@podiumhq/tracking';

import { useBusinessInfoContext } from 'contexts/BusinessContextProvider';

import {
  GoogleAutocompleteNoResults,
  GoogleAutocompleteOpenManualFormLink,
  GoogleAutocompletePrediction,
  GoogleAutocompleteResultsList,
  googleAutocompleteSmallTextStyles,
  GoogleAutocompleteWrapper,
  useGoogleAutocomplete,
} from '../GoogleAutocomplete';

const GooglePlaceSearchResults = ({
  displayManualLink,
  listRef,
  getOnKeyDown,
  resultsLimit = 5,
  setShouldHideResults,
  shouldHideResults,
  trackingEventType = 'trials',
  noResultsTitle,
  enterManuallyText,
  enterManuallyButtonProps,
  openManualForm,
}) => {
  const {
    clearPredictions,
    getAutocompletePredictions,
    hasNoResultsRef,
    placePredictions,
  } = useGoogleAutocomplete({ resultsLimit });

  const hideResults = () => setShouldHideResults(true);

  const { trackEvent } = useTracking();
  const {
    businessInfo = {},
    gmb = {},
    setGmb,
    handleGmbPredictionSelection,
    resetErrors,
    setShowManualForm,
  } = useBusinessInfoContext() || {};
  const { name: businessName } = businessInfo;

  const handleShowManualForm = () => {
    trackEvent(`${trackingEventType}.businessInfo.enterManualClick`, {
      businessName,
    });
    setGmb({});
    clearPredictions();
    resetErrors();
    hideResults();
    setShowManualForm(true);
  };

  const handlePredictionSelection = (prediction) => {
    trackEvent(`${trackingEventType}.businessInfo.gmbClick`, { prediction });
    handleGmbPredictionSelection(prediction, hideResults, trackEvent);
  };

  useEffect(() => {
    if (businessName?.length < 2) {
      clearPredictions();
      hideResults();
    } else if (gmb.googleBusinessName !== businessName) {
      getAutocompletePredictions(businessName);
      shouldHideResults && setShouldHideResults(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessName]);

  useEffect(() => {
    setShouldHideResults(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const displayPredictionResults = useMemo(
    () => businessName?.length >= 2 && !shouldHideResults,
    [businessName, shouldHideResults]
  );

  return (
    displayPredictionResults && (
      <GoogleAutocompleteWrapper hideResults={hideResults}>
        {displayManualLink && !hasNoResultsRef.current && (
          <Flex align="center">
            <Text {...googleAutocompleteSmallTextStyles} margin="0 5px 0 16px">
              Don’t see your business?
            </Text>
            <GoogleAutocompleteOpenManualFormLink
              handleShowManualForm={handleShowManualForm}
            />
            .
          </Flex>
        )}

        {placePredictions?.length > 0 && (
          <GoogleAutocompleteResultsList role="listbox" ref={listRef}>
            {placePredictions.map((prediction) => (
              <GoogleAutocompletePrediction
                key={prediction.placeId}
                description={prediction?.businessAddress}
                handlePredictionSelection={handlePredictionSelection}
                onKeyDown={getOnKeyDown}
                prediction={prediction}
                title={prediction?.googleBusinessName}
              />
            ))}
          </GoogleAutocompleteResultsList>
        )}

        {hasNoResultsRef.current && (
          <GoogleAutocompleteNoResults
            handleShowManualForm={handleShowManualForm}
            manualFormLinkSize="sm"
            title={noResultsTitle}
            enterManuallyText={enterManuallyText}
            enterManuallyButtonProps={enterManuallyButtonProps}
          />
        )}
      </GoogleAutocompleteWrapper>
    )
  );
};

export default GooglePlaceSearchResults;
